import {
	ArrowLeft,
	Building2,
	CalendarDays,
	ExternalLink,
	FileText,
	Gavel,
	Landmark,
	Layers3,
	ReceiptText,
	Trophy,
	Users,
} from "lucide-react";
import { data, Link } from "react-router";
import { z } from "zod";
import { TenderIntelligencePanel } from "~/components/tenders/tender-intelligence";
import { formatDate, formatTenderAmount } from "~/lib/format";
import { useViewer } from "~/lib/hooks/use-viewer";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import {
	fetchTenderDetail,
	fetchTenderIntelligence,
	sourceRecordCountLabel,
	TENDER_SOURCE_LABELS,
	TenderApiError,
} from "~/lib/tenders";
import type { Route } from "./+types/detail";

export async function loader({ params, request }: Route.LoaderArgs) {
	// Invalid crawler URLs are missing resources, not temporary API outages.
	if (!z.string().uuid().safeParse(params.id).success) {
		throw data(null, { status: 404 });
	}
	try {
		const [tender, intelligence] = await Promise.all([
			fetchTenderDetail(params.id, request.signal),
			fetchTenderIntelligence(params.id, request.signal).catch(() => null),
		]);
		return { tender, intelligence };
	} catch (error) {
		throw data(null, {
			status: error instanceof TenderApiError && error.status === 404 ? 404 : 503,
		});
	}
}

export function meta({ data: loaderData, params }: Route.MetaArgs) {
	const tender = loaderData?.tender;
	if (!tender) return [{ title: "Veřejná zakázka nenalezena | FirmIndex" }];
	const title = tender.title || "Veřejná zakázka";
	return generateMetaTags({
		title: `${title} | FirmIndex`,
		description: `${title}. Zadavatel ${tender.buyerName ?? "neuveden"}, hodnota ${formatTenderAmount(tender.awardedValue ?? tender.estimatedValue, tender.currency)}. Nabídky, dokumenty a zdroje zakázky.`,
		canonical: `${SITE_URL}/verejna-zakazka/${params.id}`,
	});
}

function DetailValue({
	label,
	value,
	icon: Icon,
}: {
	label: string;
	value: React.ReactNode;
	icon: typeof Gavel;
}) {
	return (
		<div className="card-surface rounded-xl p-5">
			<div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
				<Icon className="h-4 w-4 text-primary" /> {label}
			</div>
			<div className="mt-2 font-mono text-xl font-semibold tabular-nums">{value}</div>
		</div>
	);
}

function bidCountLabel(count: number): string {
	if (count === 1) return "nabídku";
	if (count >= 2 && count <= 4) return "nabídky";
	return "nabídek";
}

function TruncationNotice({ children }: { children: React.ReactNode }) {
	return (
		<p className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-muted-foreground" role="note">
			{children}
		</p>
	);
}

export default function TenderDetailPage({ loaderData }: Route.ComponentProps) {
	const { tender, intelligence } = loaderData;
	// The explorer is entitlement-gated; only send entitled viewers back to it.
	const viewer = useViewer();
	const canUseExplorer = viewer.data?.tenderExplorerEnabled === true;
	const sources = [tender, ...tender.relatedTenders];
	const displayBidValue = (value: number | null, currency: string | null) =>
		value != null && value > 0 ? formatTenderAmount(value, currency) : "–";
	// The API withholds verbatim exclusion prose by default (per-field opt-in),
	// so an excluded bidder normally arrives with no stated reason at all. Say so
	// once under the table rather than leaving a silent gap in every row.
	const exclusionReasonsWithheld =
		tender.bids.some((bid) => bid.excluded) && tender.bids.every((bid) => !bid.exclusionReason);

	return (
		<div className="min-h-screen">
			<main className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
				{canUseExplorer && (
					<Link
						to="/dashboard/zakazky"
						className="glass-control inline-flex w-fit items-center gap-1 rounded-full px-3 py-2 text-sm text-primary hover:underline"
					>
						<ArrowLeft className="h-4 w-4" /> Zpět na průzkumník zakázek
					</Link>
				)}

				<header className="sheet flex flex-col gap-5 rounded-3xl p-6 sm:p-8">
					<div className="flex flex-wrap gap-2">
						<span className="badge badge-primary badge-soft">
							{TENDER_SOURCE_LABELS[tender.source] ?? tender.source.toUpperCase()}
						</span>
						{tender.sourceCount > 1 && (
							<span className="badge badge-soft gap-1">
								<Layers3 className="h-3 w-3" /> {tender.sourceCount}{" "}
								{sourceRecordCountLabel(tender.sourceCount)}
							</span>
						)}
						{tender.hasBidPrices && (
							<span className="badge badge-success badge-soft">Obsahuje nabídkové ceny</span>
						)}
						{tender.state && <span className="badge badge-ghost">{tender.state}</span>}
					</div>
					<div className="flex flex-col gap-2">
						<p className="text-sm font-semibold text-primary">Veřejná zakázka</p>
						<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
							{tender.title || "Zakázka bez zveřejněného názvu"}
						</h1>
						{tender.buyerName && (
							<p className="flex flex-wrap items-center gap-2 text-muted-foreground">
								<Building2 className="h-4 w-4" /> Zadavatel:
								{tender.buyerIco ? (
									<Link
										to={`/firma/${tender.buyerIco}`}
										className="font-medium text-primary hover:underline"
									>
										{tender.buyerName}
									</Link>
								) : (
									<span className="font-medium text-foreground">{tender.buyerName}</span>
								)}
								{tender.buyerIco && (
									<span className="font-mono text-xs">IČO {tender.buyerIco}</span>
								)}
							</p>
						)}
					</div>
					<div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
						{tender.vvzReference && (
							<span>
								Evidence VVZ:{" "}
								<strong className="font-mono text-foreground">{tender.vvzReference}</strong>
							</span>
						)}
						<span>
							Zdrojové ID:{" "}
							<strong className="font-mono text-foreground">{tender.externalId}</strong>
						</span>
						{tender.procedureType && (
							<span>
								Řízení: <strong className="text-foreground">{tender.procedureType}</strong>
							</span>
						)}
					</div>
				</header>

				<section
					className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
					aria-label="Souhrn zakázky"
				>
					<DetailValue
						icon={Landmark}
						label="Předpokládaná hodnota"
						value={formatTenderAmount(tender.estimatedValue, tender.currency)}
					/>
					<DetailValue
						icon={Gavel}
						label="Smluvní cena"
						value={formatTenderAmount(tender.awardedValue, tender.currency)}
					/>
					<DetailValue
						icon={Users}
						label="Počet nabídek"
						value={tender.bidCount?.toLocaleString("cs-CZ") ?? "–"}
					/>
					<DetailValue
						icon={CalendarDays}
						label="Zveřejněno"
						value={formatDate(tender.publicationDate)}
					/>
				</section>

				<TenderIntelligencePanel intelligence={intelligence} />

				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr] lg:items-start">
					<div className="flex min-w-0 flex-col gap-6">
						<section className="card-surface overflow-hidden rounded-xl">
							<div className="flex items-center gap-2 p-5 pb-3">
								<Trophy className="h-5 w-5 text-primary" />
								<h2 className="text-lg font-semibold">Nabídky uchazečů</h2>
							</div>
							{tender.bids.length === 0 ? (
								<p className="px-5 pb-5 text-sm text-muted-foreground">
									Nabídkové ceny zatím nejsou ve zdrojích dostupné.
								</p>
							) : (
								<div className="overflow-x-auto">
									<table className="table">
										<thead>
											<tr>
												<th>Uchazeč</th>
												<th>Část</th>
												<th className="text-right">Nabídka bez DPH</th>
												<th>Výsledek</th>
											</tr>
										</thead>
										<tbody>
											{tender.bids.map((bid) => (
												<tr key={bid.id}>
													<td>
														<p className="font-medium">{bid.name || "Neuvedený uchazeč"}</p>
														{bid.ico && (
															<p className="font-mono text-xs text-muted-foreground">
																IČO {bid.ico}
															</p>
														)}
													</td>
													<td className="font-mono text-xs">{bid.lotNumber || "–"}</td>
													<td className="text-right font-mono tabular-nums">
														{displayBidValue(bid.valueWithoutVat, bid.currency)}
													</td>
													<td>
														{bid.isWinner ? (
															<span className="badge badge-success badge-soft badge-sm">Vítěz</span>
														) : bid.excluded ? (
															<span className="badge badge-error badge-soft badge-sm">
																Vyloučen
															</span>
														) : (
															<span className="badge badge-ghost badge-sm">Nabídka</span>
														)}
														{bid.excludedLowPrice && (
															<p className="mt-1 max-w-xs text-xs text-muted-foreground">
																Mimořádně nízká nabídková cena
															</p>
														)}
														{bid.exclusionReason && (
															<p className="mt-1 max-w-xs text-xs text-muted-foreground">
																{bid.exclusionReason}
															</p>
														)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
									{exclusionReasonsWithheld && (
										<p className="px-5 pt-3 pb-5 text-xs text-muted-foreground">
											Důvody vyloučení nezveřejňujeme — jde o doslovné citace z dokumentace
											zadavatele, které mohou obsahovat osobní údaje.
										</p>
									)}
								</div>
							)}
							{(tender.truncated?.bids || tender.truncated?.parties) && (
								<div className="px-5 pb-5">
									{tender.truncated.bids && (
										<TruncationNotice>
											Seznam nabídek je kvůli velikosti zkrácený. Počet zobrazených záznamů:{" "}
											{tender.bids.length.toLocaleString("cs-CZ")}.
											{tender.bidCount != null && (
												<>
													{" "}
													Zdroj uvádí {tender.bidCount.toLocaleString("cs-CZ")}{" "}
													{bidCountLabel(tender.bidCount)}.
												</>
											)}{" "}
											Zbývající záznamy najdete v původním zdroji.
										</TruncationNotice>
									)}
									{tender.truncated.parties && (
										<TruncationNotice>
											Seznam účastníků je kvůli velikosti zkrácený. Úplný seznam najdete v původním
											zdroji.
										</TruncationNotice>
									)}
								</div>
							)}
						</section>

						{tender.lots.length > 0 && (
							<section className="card-surface rounded-xl p-5">
								<h2 className="flex items-center gap-2 text-lg font-semibold">
									<Layers3 className="h-5 w-5 text-primary" /> Části zakázky
								</h2>
								<div className="mt-4 flex flex-col gap-3">
									{tender.lots.map((lot) => (
										<div key={lot.id} className="rounded-lg bg-gray-50 p-4">
											<p className="text-xs font-medium text-muted-foreground">
												Část {lot.lotNumber}
											</p>
											<p className="font-medium">{lot.title || "Bez názvu"}</p>
											{lot.contractDate && (
												<p className="mt-1 text-xs text-muted-foreground">
													Smlouva {formatDate(lot.contractDate)}
												</p>
											)}
										</div>
									))}
								</div>
								{tender.truncated?.lots && (
									<TruncationNotice>
										Zobrazujeme pouze první část seznamu. Úplný seznam částí najdete v původním
										zdroji.
									</TruncationNotice>
								)}
							</section>
						)}

						{tender.payments.length > 0 && (
							<section className="card-surface rounded-xl p-5">
								<h2 className="flex items-center gap-2 text-lg font-semibold">
									<ReceiptText className="h-5 w-5 text-primary" /> Skutečně uhrazené ceny
								</h2>
								<div className="mt-4 overflow-x-auto">
									<table className="table table-sm">
										<thead>
											<tr>
												<th>Rok</th>
												<th>Dodavatel</th>
												<th className="text-right">Bez DPH</th>
												<th className="text-right">S DPH</th>
											</tr>
										</thead>
										<tbody>
											{tender.payments.map((payment) => (
												<tr key={payment.id}>
													<td>{payment.year}</td>
													<td className="font-mono text-xs">{payment.ico ?? "–"}</td>
													<td className="text-right font-mono">
														{formatTenderAmount(payment.valueWithoutVat, payment.currency)}
													</td>
													<td className="text-right font-mono">
														{formatTenderAmount(payment.valueWithVat, payment.currency)}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
								{tender.truncated?.payments && (
									<TruncationNotice>
										Zobrazujeme pouze první část plateb. Úplný seznam najdete v původním zdroji.
									</TruncationNotice>
								)}
							</section>
						)}
					</div>

					<aside className="flex flex-col gap-6">
						<section className="card-surface rounded-xl p-5">
							<h2 className="text-lg font-semibold">Klasifikace</h2>
							<dl className="mt-4 flex flex-col gap-3 text-sm">
								<div>
									<dt className="text-xs text-muted-foreground">Hlavní CPV</dt>
									<dd className="font-medium">{tender.cpvLabel ?? "–"}</dd>
									<dd className="font-mono text-xs">{tender.mainCpv ?? "–"}</dd>
								</div>
								{tender.nutsCode && (
									<div>
										<dt className="text-xs text-muted-foreground">Místo plnění NUTS</dt>
										<dd className="font-mono">{tender.nutsCode}</dd>
									</div>
								)}
								{tender.regime && (
									<div>
										<dt className="text-xs text-muted-foreground">Režim</dt>
										<dd>{tender.regime}</dd>
									</div>
								)}
								{tender.awardDate && (
									<div>
										<dt className="text-xs text-muted-foreground">Rozhodnutí</dt>
										<dd>{formatDate(tender.awardDate)}</dd>
									</div>
								)}
								{tender.contractDate && (
									<div>
										<dt className="text-xs text-muted-foreground">Uzavření smlouvy</dt>
										<dd>{formatDate(tender.contractDate)}</dd>
									</div>
								)}
							</dl>
						</section>

						<section className="card-surface rounded-xl p-5">
							<h2 className="flex items-center gap-2 text-lg font-semibold">
								<FileText className="h-5 w-5 text-primary" /> Dokumenty
							</h2>
							{tender.documents.length === 0 ? (
								<p className="mt-3 text-sm text-muted-foreground">Žádné propojené dokumenty.</p>
							) : (
								<ul className="mt-3 flex flex-col gap-2">
									{tender.documents.map((document) => (
										<li key={document.id}>
											<a
												href={document.url}
												target="_blank"
												rel="noopener noreferrer nofollow"
												className="flex items-start gap-2 text-sm text-primary hover:underline"
											>
												<ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
												<span>{document.name || document.docType || "Dokument zakázky"}</span>
											</a>
											{document.extractionStatus && (
												<span className="ml-5 text-xs text-muted-foreground">
													Extrakce: {document.extractionStatus}
												</span>
											)}
										</li>
									))}
								</ul>
							)}
							{tender.truncated?.documents && (
								<TruncationNotice>
									Zobrazujeme pouze první část dokumentů. Úplný seznam najdete v původním zdroji.
								</TruncationNotice>
							)}
						</section>

						<section className="card-surface rounded-xl p-5">
							<h2 className="flex items-center gap-2 text-lg font-semibold">
								<Layers3 className="h-5 w-5 text-primary" /> Zdroje dat
							</h2>
							<p className="mt-1 text-xs text-muted-foreground">
								Záznamy se stejnou evidencí VVZ jsou na přehledech sloučené. Zde zůstávají
								dohledatelné jednotlivě.
							</p>
							<ul className="mt-3 flex flex-col gap-2">
								{sources.map((source) => (
									<li
										key={source.id}
										className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 p-3 text-sm"
									>
										<span>
											<strong>
												{TENDER_SOURCE_LABELS[source.source] ?? source.source.toUpperCase()}
											</strong>
											<span className="block font-mono text-xs text-muted-foreground">
												{source.externalId}
											</span>
										</span>
										{source.id !== tender.id && (
											<Link
												to={`/verejna-zakazka/${source.id}`}
												className="text-primary hover:underline"
											>
												Otevřít
											</Link>
										)}
									</li>
								))}
							</ul>
							{tender.sourceUrl && (
								<a
									href={tender.sourceUrl}
									target="_blank"
									rel="noopener noreferrer nofollow"
									className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline"
								>
									Původní záznam <ExternalLink className="h-3.5 w-3.5" />
								</a>
							)}
							{tender.profile?.url && (
								<a
									href={tender.profile.url}
									target="_blank"
									rel="noopener noreferrer nofollow"
									className="mt-2 flex items-center gap-1 text-sm text-primary hover:underline"
								>
									Profil zadavatele <ExternalLink className="h-3.5 w-3.5" />
								</a>
							)}
						</section>
					</aside>
				</div>
			</main>
		</div>
	);
}
